import { useEffect, useRef, useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Button,
  Checkbox,
  FileButton,
  Group,
  Image,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Link } from 'react-router';
import { GOZLEM_TIP_ETIKETI } from '@isg/validation';
import type { Gozlem, GozlemTipi, SahaTuruDetay } from '@isg/validation';
import {
  useEkipAdaylari,
  useGozlemEkle,
  useSahaTuruEkIndir,
  useSahaTuruEkSil,
  useSahaTuruEkYukle,
  useGozlemFotografIndir,
  useGozlemFotografSil,
  useGozlemFotografYukle,
  useGozlemSil,
  useSahaTuruEkipCikar,
  useSahaTuruEkipEkle,
  useSahaTuruGozlemler,
  useSahaTuruKapat,
} from '../api/queries';
import { QueryStatus } from './parts';
import {
  Bolum,
  KapiSebebi,
  Rozet,
  SatirEylemleri,
  SatirSilmeSebebi,
  Sayfalama,
  SolukSatir,
} from '../sozlesme';
import { zamanGoster } from '../lib/format';
import { ANLAM } from '../theme';

/**
 * SAHA TURU DETAYININ BÖLÜMLERİ (Kapsam §27; sözleşme P1-a · P2 · P11).
 *
 * ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler` — §2.40): ekran şapka
 * listesi ya da bahçe türetmez; kapalı turda yazma denetimleri HİÇ çizilmez.
 * ⚠️ Hata sessizce yutulmaz: her yazma kancasının hatası `role="alert"`
 * bandında görünür (D1).
 */

// ── EKİP (S8) ─────────────────────────────────────────────────────────────

export function EkipBolumu({ tur }: { tur: SahaTuruDetay }): React.JSX.Element {
  const ekle = useSahaTuruEkipEkle();
  const cikar = useSahaTuruEkipCikar();
  const [arama, setArama] = useState('');
  const [secilen, setSecilen] = useState<string | null>(null);
  const adaylar = useEkipAdaylari(tur.yetkiler.yazabilir ? tur.id : undefined, arama);
  const mevcut = new Set(tur.ekip.map((u) => u.personId));
  const hata = ekle.error ?? cikar.error;
  return (
    <Bolum baslik="Ekip" testId="saha-turu-ekip">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="saha-turu-ekip-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <Table data-testid="saha-turu-ekip-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Ad Soyad</Table.Th>
            <Table.Th w={120}>Görev</Table.Th>
            <Table.Th w={80} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tur.ekip.map((u) => (
            <Table.Tr key={u.id} data-testid={`saha-turu-ekip-${u.personId}`}>
              <Table.Td>{u.adSoyad}</Table.Td>
              <Table.Td>
                <Rozet anlam="bilgi">{u.lider ? 'Lider' : 'Ekip üyesi'}</Rozet>
              </Table.Td>
              <Table.Td>
                {tur.yetkiler.yazabilir && !u.lider && (
                  <SatirEylemleri
                    testId={`saha-turu-ekip-eylemler-${u.personId}`}
                    digerleri={[
                      {
                        etiket: 'Ekipten çıkar',
                        tehlikeli: true,
                        testId: `saha-turu-ekip-cikar-${u.personId}`,
                        onClick: () => cikar.mutate({ id: tur.id, memberId: u.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {tur.yetkiler.yazabilir && (
            <SolukSatir
              testId="saha-turu-ekip-soluk"
              dokunuldu={secilen !== null || arama.length > 0}
              kaydedilebilir={secilen !== null}
              kaydediliyor={ekle.isPending}
              kolonSayisi={2}
              onKaydet={() => {
                if (!secilen) return;
                ekle.mutate(
                  { id: tur.id, personId: secilen },
                  {
                    onSuccess: () => {
                      setSecilen(null);
                      setArama('');
                    },
                  },
                );
              }}
            >
              <Table.Td colSpan={2}>
                {/* ⚠️ `withinPortal` ZORUNLU: Bolum bir Card'dır ve portalsız
                    açılır liste tablo başlığının arkasında kalır (tur 4½ / B1). */}
                <Select
                  size="xs"
                  placeholder="+ Ekip üyesi ekle (ad ile ara)"
                  aria-label="Ekip üyesi ekle"
                  searchable
                  clearable
                  comboboxProps={{ withinPortal: true }}
                  searchValue={arama}
                  onSearchChange={setArama}
                  value={secilen}
                  onChange={setSecilen}
                  nothingFoundMessage="Bu işyerinde eşleşen çalışan yok"
                  data={(adaylar.data ?? [])
                    .filter((a) => !mevcut.has(a.personId))
                    .map((a) => ({ value: a.personId, label: a.adSoyad }))}
                  data-testid="saha-turu-ekip-secici"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
    </Bolum>
  );
}

// ── GÖZLEMLER (S3) ────────────────────────────────────────────────────────

export function GozlemBolumu({ tur }: { tur: SahaTuruDetay }): React.JSX.Element {
  const [sayfa, setSayfa] = useState(1);
  const liste = useSahaTuruGozlemler(tur.id, sayfa);
  const ekle = useGozlemEkle();
  const sil = useGozlemSil();
  const fotoYukle = useGozlemFotografYukle();
  const fotoSil = useGozlemFotografSil();
  const [tip, setTip] = useState<GozlemTipi>('uygunsuz');
  const [metin, setMetin] = useState('');
  // P12 (madde 224) — kaydedilmemiş gözlem satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(metin.trim().length > 0 ? 1 : 0, 'gözlem satırı');
  const hata = ekle.error ?? sil.error ?? fotoYukle.error ?? fotoSil.error;
  const kaydet = (): void => {
    if (!metin.trim()) return;
    ekle.mutate(
      { id: tur.id, girdi: { tip, metin: metin.trim() } },
      {
        onSuccess: () => {
          setMetin('');
          setTip('uygunsuz');
        },
      },
    );
  };
  return (
    <Bolum baslik="Gözlemler" testId="saha-turu-gozlemler">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="saha-turu-gozlem-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <QueryStatus queries={[liste]} />
      <Table data-testid="saha-turu-gozlem-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}>#</Table.Th>
            <Table.Th w={120}>Tip</Table.Th>
            <Table.Th>Gözlem</Table.Th>
            <Table.Th w={160}>Atıf</Table.Th>
            <Table.Th w={220}>Fotoğraf</Table.Th>
            <Table.Th w={160}>Uygunsuzluk kaydı</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(liste.data?.satirlar ?? []).map((g) => (
            <GozlemSatiri
              key={g.id}
              tur={tur}
              gozlem={g}
              onSil={() => sil.mutate({ id: tur.id, observationId: g.id })}
              onFoto={(dosya) => fotoYukle.mutate({ id: tur.id, observationId: g.id, dosya })}
              onFotoSil={(attachmentId) =>
                fotoSil.mutate({ id: tur.id, observationId: g.id, attachmentId })
              }
            />
          ))}
          {tur.yetkiler.yazabilir && (
            <SolukSatir
              testId="saha-turu-gozlem-soluk"
              dokunuldu={metin.trim().length > 0}
              kaydedilebilir={metin.trim().length > 0}
              kaydediliyor={ekle.isPending}
              kolonSayisi={6}
              onKaydet={kaydet}
            >
              <Table.Td />
              <Table.Td>
                <Select
                  size="xs"
                  aria-label="Gözlem tipi"
                  comboboxProps={{ withinPortal: true }}
                  allowDeselect={false}
                  value={tip}
                  onChange={(v) => setTip((v as GozlemTipi) ?? 'uygunsuz')}
                  data={Object.entries(GOZLEM_TIP_ETIKETI).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  data-testid="saha-turu-gozlem-tip"
                />
              </Table.Td>
              <Table.Td colSpan={4}>
                <TextInput
                  size="xs"
                  placeholder="+ Gözlem ekle"
                  aria-label="Yeni gözlem"
                  value={metin}
                  onChange={(e) => setMetin(e.currentTarget.value)}
                  data-testid="saha-turu-gozlem-metin"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {tur.durum === 'acik' && (
        <KapiSebebi
          durum={tur.yetkiler.yazmaKapisi}
          nesne="Gözlemi"
          fiil="ekler"
          testId="saha-turu-gozlem-yazamama-sebebi"
        />
      )}
      {liste.isSuccess && liste.data.toplam > liste.data.sayfaBoyutu && (
        <Sayfalama
          sayfa={liste.data.sayfa}
          sayfaBoyutu={liste.data.sayfaBoyutu}
          toplam={liste.data.toplam}
          onDegis={setSayfa}
          testId="saha-turu-gozlem-sayfalama"
        />
      )}
    </Bolum>
  );
}

function GozlemSatiri({
  tur,
  gozlem,
  onSil,
  onFoto,
  onFotoSil,
}: {
  tur: SahaTuruDetay;
  gozlem: Gozlem;
  onSil: () => void;
  onFoto: (dosya: File) => void;
  onFotoSil: (attachmentId: string) => void;
}): React.JSX.Element {
  const indir = useGozlemFotografIndir();
  return (
    <Table.Tr data-testid={`saha-turu-gozlem-${gozlem.id}`}>
      <Table.Td>{gozlem.sira}</Table.Td>
      <Table.Td>
        <Rozet anlam={gozlem.tip === 'olumlu' ? 'olumlu' : 'dikkat'}>
          {GOZLEM_TIP_ETIKETI[gozlem.tip]}
        </Rozet>
      </Table.Td>
      <Table.Td>
        <Text size="sm" data-testid={`saha-turu-gozlem-metin-${gozlem.id}`}>
          {gozlem.metin}
        </Text>
        {gozlem.alanKodu && (
          <Text size="xs" c="dimmed">
            Form maddesi
          </Text>
        )}
      </Table.Td>
      <Table.Td>{gozlem.atif ?? '—'}</Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="wrap">
          {gozlem.fotograflar.map((f) => (
            <GozlemFotografi
              key={f.id}
              dosyaAdi={f.dosyaAdi}
              yukle={() => indir(tur.id, gozlem.id, f.id).then((s) => s.blob)}
              // P13-c (madde 227) — silme kapısı fotoğrafın kendisinde (sunucudan):
              // tur yazma → kapalı tur → sahiplik (yükleyen VEYA holding yön.).
              onSil={f.silmeKapisi.yazabilir ? () => onFotoSil(f.id) : undefined}
              testId={`saha-turu-foto-${f.id}`}
            />
          ))}
          {tur.yetkiler.yazabilir && (
            <FileButton onChange={(d) => d && onFoto(d)} accept="image/jpeg,image/png,image/heic">
              {(props) => (
                <Button
                  {...props}
                  size="compact-xs"
                  variant="light"
                  data-testid={`saha-turu-foto-ekle-${gozlem.id}`}
                >
                  Fotoğraf ekle
                </Button>
              )}
            </FileButton>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {gozlem.nonconformityId ? (
          <Group gap={6} wrap="nowrap">
            <Link
              to={`/uygunsuzluklar/${gozlem.nonconformityId}`}
              data-testid={`saha-turu-bulgu-link-${gozlem.id}`}
            >
              {gozlem.nonconformityTakipNo}
            </Link>
            <Rozet anlam={gozlem.bulguKapali ? 'olumlu' : 'engel'}>
              {gozlem.bulguKapali ? 'Kapalı' : 'Açık'}
            </Rozet>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {tur.yetkiler.yazabilir && (
          <SatirEylemleri
            testId={`saha-turu-gozlem-eylemler-${gozlem.id}`}
            digerleri={[
              {
                etiket: 'Gözlemi sil',
                tehlikeli: true,
                testId: `saha-turu-gozlem-sil-${gozlem.id}`,
                onClick: onSil,
              },
            ]}
          />
        )}
      </Table.Td>
    </Table.Tr>
  );
}

/** Küçük görsel — baytlar API'den, object URL bileşen ömrüyle sınırlı (form-parts `EkOnizleme` deseni). */
function GozlemFotografi({
  dosyaAdi,
  yukle,
  onSil,
  testId,
}: {
  dosyaAdi: string;
  yukle: () => Promise<Blob>;
  onSil?: () => void;
  testId: string;
}): React.JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const yukleyici = useRef(yukle);
  yukleyici.current = yukle;
  useEffect(() => {
    let iptal = false;
    let acilan: string | null = null;
    void yukleyici.current().then((blob) => {
      if (iptal) return;
      acilan = URL.createObjectURL(blob);
      setUrl(acilan);
    });
    return () => {
      iptal = true;
      if (acilan) URL.revokeObjectURL(acilan);
    };
  }, [testId]);
  return (
    <Stack gap={2} data-testid={testId}>
      {url === null ? (
        <Text size="xs" c="dimmed">
          {dosyaAdi}
        </Text>
      ) : (
        <Image src={url} alt={dosyaAdi} w={72} h={72} fit="cover" radius="sm" />
      )}
      {onSil && (
        <Button size="compact-xs" variant="subtle" color={ANLAM.engel} onClick={onSil}>
          Kaldır
        </Button>
      )}
    </Stack>
  );
}

// ── KAPANIŞ (S4 · S9) — BULGU SEÇİMİ ──────────────────────────────────────

/**
 * Lider seçer: hangi UYGUNSUZ gözlemler ve hangi ✗ maddeler uygunsuzluk
 * kaydı olsun. Seçilmeyen ✗ form cevabı olarak kalır. Bulgusuz kapanış da
 * meşrudur. ⚠️ Gözlemler en çok 100 satırla okunur (sunucu tavanı); tur bu
 * ölçeği aşıyorsa seçim listesi ilk yüzle sınırlıdır — ekranda söylenir.
 */
export function KapanisBolumu({
  tur,
  onKapandi,
  onVazgec,
}: {
  tur: SahaTuruDetay;
  onKapandi: () => void;
  onVazgec: () => void;
}): React.JSX.Element {
  const gozlemler = useSahaTuruGozlemler(tur.id, 1, 100);
  const kapat = useSahaTuruKapat();
  const [gozlemSecimi, setGozlemSecimi] = useState<Set<string>>(new Set());
  const [maddeSecimi, setMaddeSecimi] = useState<Set<string>>(new Set());
  const uygunsuzGozlemler = (gozlemler.data?.satirlar ?? []).filter(
    (g) => g.tip === 'uygunsuz' && g.alanKodu === null,
  );
  const maddeler = tur.denetimFormu?.uygunsuzMaddeler ?? [];
  const toggle = (set: Set<string>, id: string): Set<string> => {
    const yeni = new Set(set);
    if (yeni.has(id)) yeni.delete(id);
    else yeni.add(id);
    return yeni;
  };
  return (
    <Bolum
      baslik="Turu kapat — uygunsuzluk kaydı olacak bulguları seçin"
      testId="saha-turu-kapanis"
    >
      {kapat.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="saha-turu-kapanis-hata" mb="xs">
          {kapat.error.message}
        </Alert>
      )}
      <QueryStatus queries={[gozlemler]} />
      <Stack gap="md">
        {tur.tip === 'denetim' && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Formda uygunsuz cevaplanan maddeler
            </Text>
            {maddeler.length === 0 ? (
              <Text size="sm" c="dimmed">
                Formda uygunsuz madde yok.
              </Text>
            ) : (
              maddeler.map((m) => (
                <Checkbox
                  key={m.alanKodu}
                  size="sm"
                  label={`${m.etiket} — ${m.cevapEtiketi}${m.atif ? ` (${m.atif})` : ''}`}
                  checked={maddeSecimi.has(m.alanKodu)}
                  onChange={() => setMaddeSecimi((s) => toggle(s, m.alanKodu))}
                  data-testid={`saha-turu-kapanis-madde-${m.alanKodu}`}
                />
              ))
            )}
          </Stack>
        )}
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Uygunsuz gözlemler
          </Text>
          {uygunsuzGozlemler.length === 0 ? (
            <Text size="sm" c="dimmed">
              Uygunsuz gözlem yok.
            </Text>
          ) : (
            uygunsuzGozlemler.map((g) => (
              <Checkbox
                key={g.id}
                size="sm"
                label={`${g.sira}. ${g.metin}`}
                checked={gozlemSecimi.has(g.id)}
                onChange={() => setGozlemSecimi((s) => toggle(s, g.id))}
                data-testid={`saha-turu-kapanis-gozlem-${g.id}`}
              />
            ))
          )}
          {gozlemler.isSuccess && gozlemler.data.toplam > gozlemler.data.sayfaBoyutu && (
            <Text size="xs" c="dimmed">
              Yalnız ilk {gozlemler.data.sayfaBoyutu} gözlem listeleniyor.
            </Text>
          )}
        </Stack>
        <Group justify="flex-end">
          <Button variant="default" onClick={onVazgec} data-testid="saha-turu-kapanis-vazgec">
            Vazgeç
          </Button>
          <Button
            data-testid="saha-turu-kapanis-onayla"
            loading={kapat.isPending}
            onClick={() =>
              kapat.mutate(
                {
                  id: tur.id,
                  secim: {
                    bulguGozlemIds: [...gozlemSecimi],
                    bulguFormMaddeleri: [...maddeSecimi],
                  },
                },
                { onSuccess: onKapandi },
              )
            }
          >
            {gozlemSecimi.size + maddeSecimi.size > 0
              ? `Kapat ve ${gozlemSecimi.size + maddeSecimi.size} uygunsuzluk kaydı aç`
              : 'Bulgusuz kapat'}
          </Button>
        </Group>
      </Stack>
    </Bolum>
  );
}

// ── TUR EKLERİ (E5 — dış tatbikat raporu; G2.2 — tatbikat/tur fotoğrafı) ──

/** Küçük resimle gösterilecek ek türleri — PDF'in gömdüğü kümeyle aynı (jpeg · png). */
const GORSEL_MIME = new Set(['image/jpeg', 'image/png']);

/**
 * Dış rapor tutanak PDF ile YAN YANA durur (ürün sahibi kararı C). ⚠️ Kapalı
 * turda da yüklenir — rapor tatbikattan sonra gelir; görünürlük
 * `yetkiler.ekYukleyebilir`den (sunucu). Silen yükleyen (422 sunucuda).
 */
export function EkBolumu({ tur }: { tur: SahaTuruDetay }): React.JSX.Element {
  const yukle = useSahaTuruEkYukle();
  const sil = useSahaTuruEkSil();
  const indir = useSahaTuruEkIndir();
  const hata = yukle.error ?? sil.error;
  // Madde 230 — kapalı turda tur ekini silmek GEREKÇE ister; bayrak sunucudan
  // (`ekSilmeGerekceZorunlu`), ekran "kapalı mı" kuralını yeniden yazmaz.
  const [silinecek, setSilinecek] = useState<string | null>(null);
  const [gerekce, setGerekce] = useState('');
  const gerekceZorunlu = tur.yetkiler.ekSilmeGerekceZorunlu;
  return (
    <Bolum baslik="Ekler (fotoğraf · dış rapor)" testId="saha-turu-ekler">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="saha-turu-ek-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      {/* P13-c — sebep bölüm başına BİR KEZ (ilk kapalı satır). ⚠️ Dış rapor kapalı tura
          da yüklenir ve tur yeniden açılmaz → o ek kalıcıdır; cümle bunu söyler
          ("Kapalı turun eki silinemez"). */}
      <SatirSilmeSebebi
        kapilar={tur.ekler.map((ek) => ek.silmeKapisi)}
        nesne="Eki"
        fiil="kaldırır"
        testId="saha-turu-ek-silme-sebebi"
      />
      <Group gap="sm" wrap="wrap">
        {tur.ekler.map((ek) => (
          <Group gap={6} wrap="nowrap" key={ek.id} data-testid={`saha-turu-ek-${ek.id}`}>
            {/* G2.2 — görsel ek küçük resimle (tatbikat fotoğrafı); aynı tablo, aynı akış. */}
            {GORSEL_MIME.has(ek.mimeType) && (
              <GozlemFotografi
                dosyaAdi={ek.dosyaAdi}
                yukle={async () => (await indir(tur.id, ek.id)).blob}
                testId={`saha-turu-ek-foto-${ek.id}`}
              />
            )}
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() =>
                void indir(tur.id, ek.id).then(({ blob, dosyaAdi }) => {
                  const url = URL.createObjectURL(blob);
                  const bag = document.createElement('a');
                  bag.href = url;
                  bag.download = dosyaAdi ?? ek.dosyaAdi;
                  document.body.appendChild(bag);
                  bag.click();
                  bag.remove();
                  URL.revokeObjectURL(url);
                })
              }
            >
              {ek.dosyaAdi}
            </Button>
            <Text size="xs" c="dimmed">
              {ek.yukleyenEtiket}
            </Text>
            {/* P13-c (madde 227) — silme kapısı SATIR BAŞINA sunucudan. */}
            {ek.silmeKapisi.yazabilir && silinecek !== ek.id && (
              <Button
                size="compact-xs"
                variant="subtle"
                color={ANLAM.engel}
                data-testid={`saha-turu-ek-sil-${ek.id}`}
                onClick={() =>
                  gerekceZorunlu
                    ? setSilinecek(ek.id)
                    : sil.mutate({ id: tur.id, attachmentId: ek.id })
                }
              >
                Kaldır
              </Button>
            )}
            {silinecek === ek.id && (
              <Group gap="xs" wrap="nowrap" data-testid="saha-turu-ek-gerekce-satiri">
                <TextInput
                  size="xs"
                  placeholder="Kaldırma gerekçesi (zorunlu)"
                  aria-label="Kaldırma gerekçesi"
                  value={gerekce}
                  onChange={(ev) => setGerekce(ev.currentTarget.value)}
                  data-testid="saha-turu-ek-gerekce"
                  style={{ flex: 1 }}
                />
                <Button
                  size="xs"
                  color={ANLAM.engel}
                  data-testid={`saha-turu-ek-sil-onayla-${ek.id}`}
                  disabled={gerekce.trim().length < 3}
                  loading={sil.isPending}
                  onClick={() =>
                    sil.mutate(
                      { id: tur.id, attachmentId: ek.id, gerekce: gerekce.trim() },
                      {
                        onSuccess: () => {
                          setSilinecek(null);
                          setGerekce('');
                        },
                      },
                    )
                  }
                >
                  Kaldır
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setSilinecek(null)}>
                  Vazgeç
                </Button>
              </Group>
            )}
          </Group>
        ))}
        {/* Madde 230 — kaldırılan tur eklerinin izi (kim · ne zaman · gerekçe); sunucudan. */}
        {tur.silinenEkler.map((s) => (
          <Text key={s.id} size="xs" c="dimmed" data-testid={`saha-turu-ek-silinen-${s.id}`}>
            Kaldırıldı: {s.dosyaAdi} — {s.silenEtiket ?? 'Hesap'} · {zamanGoster(s.silindiAt)}
            {s.gerekce ? ` · ${s.gerekce}` : ''}
          </Text>
        ))}
        {tur.ekler.length === 0 && (
          <Text size="sm" c="dimmed">
            Ek yok.
          </Text>
        )}
        {tur.yetkiler.ekYukleyebilir && (
          <FileButton
            onChange={(d) => d && yukle.mutate({ id: tur.id, dosya: d })}
            accept="application/pdf,image/jpeg,image/png"
          >
            {(props) => (
              <Button
                {...props}
                size="compact-xs"
                variant="light"
                loading={yukle.isPending}
                data-testid="saha-turu-ek-yukle"
              >
                Fotoğraf / dış rapor yükle
              </Button>
            )}
          </FileButton>
        )}
      </Group>
      <KapiSebebi
        durum={tur.yetkiler.yazmaKapisi}
        nesne="Tur ekini"
        fiil="yükler"
        testId="saha-turu-ek-yazamama-sebebi"
      />
    </Bolum>
  );
}
